// Attributions d'emplacements (H5-01, lot L2-B) : commerçant (usager du portail ou tiers saisi au
// guichet), période, périodicité des redevances, demande d'origine (dossier de la démarche
// « emplacement de marché » décidé favorablement, ou saisie directe au guichet), résiliation
// motivée, renouvellement. Le serveur refuse toute attribution chevauchant une période en vigueur
// sur le même emplacement (contrainte d'exclusion en base, R7). Échéancier : découpage pur de la
// période en périodes de redevance contiguës (le montant de chacune est calculé par le module des
// recettes, jamais ici).
import { z } from 'zod';
import { CODES_PERIODICITES, PERIODICITES_REDEVANCE, type PeriodiciteRedevance } from './marches-structure';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');

export const ETATS_ATTRIBUTION = ['attribuee', 'active', 'resiliee', 'terminee'] as const;
export type EtatAttribution = (typeof ETATS_ATTRIBUTION)[number];
/** États « en vigueur » couverts par la contrainte d'exclusion (période à venir ou en cours). */
export const ETATS_ATTRIBUTION_EN_VIGUEUR: EtatAttribution[] = ['attribuee', 'active'];

/** Durée maximale d'une attribution (hypothèse H-L2B-03 : trois ans, renouvelable). */
export const DUREE_MAX_ATTRIBUTION_JOURS = 3 * 366;
/** Proposition de renouvellement : 30 jours avant la fin (H5-01). */
export const PREAVIS_RENOUVELLEMENT_JOURS = 30;
/** Échéance d'une redevance : 15 jours après le début de sa période, ou après son émission si elle est plus tardive (hypothèse H-L2B-04). */
export const DELAI_ECHEANCE_REDEVANCE_JOURS = 15;

export const NATURE_OBJET_ATTRIBUTION = 'attribution_emplacement' as const;

const periode = z
  .strictObject({ debut: jour, fin: jour })
  .refine((p) => p.fin >= p.debut, { message: 'La fin suit le début', path: ['fin'] })
  .refine((p) => (Date.parse(p.fin) - Date.parse(p.debut)) / 86_400_000 < DUREE_MAX_ATTRIBUTION_JOURS, { message: 'Durée de trois ans au plus', path: ['fin'] });

export const schemaCommercant = z
  .strictObject({
    usager_compte_id: uuid.optional(),
    nom: z.string().trim().min(2).max(200).optional(),
    type: z.enum(['personne', 'entreprise']).default('personne'),
    telephone: z.string().trim().regex(/^\+?[0-9][0-9 .-]{5,19}$/, 'Numéro de téléphone invalide').optional(),
  })
  .refine((c) => c.usager_compte_id || c.nom, { message: 'Indiquer le compte usager ou le nom du commerçant', path: ['nom'] });

export const schemaCreationAttribution = z
  .strictObject({
    emplacement_id: uuid,
    periode,
    periodicite: z.enum(CODES_PERIODICITES).optional(),
    /** Dossier de la démarche « emplacement de marché » décidé favorablement (le commerçant en est le demandeur). */
    dossier_id: uuid.optional(),
    /** Saisie directe au guichet : commerçant désigné par l'agent. */
    commercant: schemaCommercant.optional(),
    activite: z.string().trim().min(2).max(200).optional(),
    /** Référence de l'acte de décision (arrêté, décision du maire), facultative. */
    decision_reference: z.string().trim().max(120).optional(),
    observations: z.string().trim().max(1000).optional(),
  })
  .refine((v) => Boolean(v.dossier_id) !== Boolean(v.commercant), { message: 'Indiquer soit le dossier de la demande, soit le commerçant saisi au guichet', path: ['commercant'] });

export const schemaResiliationAttribution = z.strictObject({
  motif: z.string().trim().min(5).max(1000),
  /** Date d'effet : l'emplacement est libre à partir de ce jour (défaut : aujourd'hui). */
  date_effet: jour.optional(),
});

export const schemaRenouvellementAttribution = z.strictObject({
  /** Fin de la nouvelle période (défaut : même durée que la période renouvelée). */
  fin: jour.optional(),
  periodicite: z.enum(CODES_PERIODICITES).optional(),
  decision_reference: z.string().trim().max(120).optional(),
});

export const schemaFiltreAttributions = z.strictObject({
  marche: uuid.optional(),
  emplacement: uuid.optional(),
  etat: z.enum(ETATS_ATTRIBUTION).optional(),
  en_vigueur: z.enum(['oui', 'non']).optional(),
  /** Attributions en vigueur dont la fin tombe dans les 30 jours (proposition de renouvellement). */
  a_renouveler: z.enum(['oui', 'non']).optional(),
  q: z.string().trim().min(2).max(100).optional(),
  limite: z.coerce.number().int().min(1).max(500).default(100),
});

// Échéancier --------------------------------------------------------------------------------

export interface PeriodeRedevance {
  rang: number;
  debut: string;
  fin: string;
}

const versDate = (j: string) => new Date(`${j}T00:00:00Z`);
const versJour = (d: Date) => d.toISOString().slice(0, 10);

/** Date augmentée de n mois civils ; le jour est ramené au dernier jour du mois si nécessaire (31 janvier + 1 mois = 28 ou 29 février). */
export function ajouterMois(j: string, n: number): string {
  const d = versDate(j);
  const cible = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
  const dernier = new Date(Date.UTC(cible.getUTCFullYear(), cible.getUTCMonth() + 1, 0)).getUTCDate();
  cible.setUTCDate(Math.min(d.getUTCDate(), dernier));
  return versJour(cible);
}

export function ajouterJours(j: string, n: number): string {
  return versJour(new Date(versDate(j).getTime() + n * 86_400_000));
}

/**
 * Périodes de redevance contiguës d'une attribution : la première commence au début de
 * l'attribution, chacune dure un mois ou un trimestre civil compté depuis ce début, la dernière est
 * tronquée à la fin de l'attribution.
 */
export function decouperPeriodes(debut: string, fin: string, periodicite: PeriodiciteRedevance): PeriodeRedevance[] {
  if (fin < debut) return [];
  const pas = PERIODICITES_REDEVANCE[periodicite];
  const periodes: PeriodeRedevance[] = [];
  for (let rang = 1; rang <= 200; rang += 1) {
    const d = ajouterMois(debut, (rang - 1) * pas);
    if (d > fin) break;
    const f = ajouterJours(ajouterMois(debut, rang * pas), -1);
    periodes.push({ rang, debut: d, fin: f < fin ? f : fin });
  }
  return periodes;
}

/** Échéance d'une redevance : délai compté depuis le début de sa période, ou depuis son émission si elle est plus tardive. */
export function echeanceRedevance(debutPeriode: string, emiseLe: string, delaiJours = DELAI_ECHEANCE_REDEVANCE_JOURS): string {
  return ajouterJours(debutPeriode > emiseLe ? debutPeriode : emiseLe, delaiJours);
}
